import React, { useEffect } from 'react'
import { inject, observer } from 'mobx-react'
import { last, minBy, findIndex, first } from 'lodash-es'
import gsap from 'gsap'
import { StateType } from '../state'
import { useAnimationLoop } from '../../../lib/hooks/useAnimationLoop'
import { drawYearsLabel, drawYearsLegend } from '../lib/draw-canvas'
import { scaleByDevicePixelRatio, circle as drawCircle } from '../../../lib/canvas-utils'
import { getMousePosition } from '../../../lib/event-utils'
import { circlesYears, MAX_RADIUS_YEARS } from '../data'
import { CHALLENGES, YEARS_EXTENT } from '../../../lib/constants'
import { SliderWithLabels } from '../../../components/ui/SliderWithLabels'
import { Container } from './Container'

const DEBUG = false
const DIMENSIONS = { w: 400, h: 400 }
const ANIMATION_DURATION = 0.75

interface Props {
  state?: StateType
}

export const CirclesYears = inject('state')(
  observer(({ state }: Props) => {
    const { Circles, YearsState } = state
    const { challenge } = Circles
    const { setIsAnimationRunning, selectedYear, setSelectedYear, setHoveredYear } = YearsState

    const canvasRef = React.useRef<HTMLCanvasElement>()
    const circlesAllChallenges = React.useRef(
      Object.fromEntries(CHALLENGES.map((c) => [c, circlesYears(c, DIMENSIONS)]))
    ).current
    const circles = circlesAllChallenges[challenge] as ReturnType<typeof circlesYears>

    const circleLabels = circles.map((circle) => {
      const { x, y } = minBy(circles, (c) => c.radius)
      return { ...circle, opacity: 1, x, y }
    })
    const animatedLabel = React.useRef({ ...circles[0], opacity: 1 }).current
    const minRadiusCircle = minBy(circles, 'radius')

    function setup() {
      const ctx = canvasRef.current?.getContext('2d')
      scaleByDevicePixelRatio(canvasRef.current, ctx, DIMENSIONS.w, DIMENSIONS.h)

      setIsAnimationRunning(true)

      gsap
        .timeline({
          onStart: () => startLoop(),
          onComplete: () => {
            stopLoop()
            setIsAnimationRunning(false)
          },
        })
        .addLabel('start')
        .to(circles, {
          opacity: 1,
          stagger: { amount: ANIMATION_DURATION / 2, ease: 'power1.out' },
        })
        .to(circles, {
          delay: -ANIMATION_DURATION * 0.9,
          opacity: (_, c) => (c.year !== selectedYear ? 0.1 : 1),
          stagger: { amount: ANIMATION_DURATION / 2, ease: 'power1.out' },
        })
        .fromTo(
          animatedLabel,
          {
            year: first(circleLabels).year,
            color: first(circleLabels).color,
          },
          {
            year: last(circleLabels).year,
            color: last(circleLabels).color,
            roundProps: 'year',
            duration: ANIMATION_DURATION,
          },
          'start'
        )
    }

    function findPointedCircle(xPos: number, yPos: number) {
      const circlesWithDistance = circles.map((circle) => {
        const { x, y, radius } = circle
        const dist = Math.sqrt((x - xPos) ** 2 + (y - yPos) ** 2)
        const isOuterViz = dist >= MAX_RADIUS_YEARS + 2
        const isInnerViz = dist <= minRadiusCircle.radius - 2
        return {
          circle,
          distance: Math.abs(radius - dist),
          isOuterViz: isOuterViz || isInnerViz,
        }
      })
      const nearestCircle = minBy(circlesWithDistance, 'distance')
      const pointedCircle = nearestCircle.isOuterViz ? null : nearestCircle.circle

      if (DEBUG) {
        const ctx = canvasRef.current?.getContext('2d')
        ctx.save()
        ctx.strokeStyle = 'blue'
        ctx.strokeRect(
          pointedCircle.x - pointedCircle.radius,
          pointedCircle.y - pointedCircle.radius,
          pointedCircle.radius * 2,
          pointedCircle.radius * 2
        )
        ctx.restore()
      }

      return pointedCircle
    }

    function handleInteraction(
      event: React.MouseEvent<HTMLCanvasElement, MouseEvent>,
      ref: React.MutableRefObject<HTMLCanvasElement>,
      setState: (year: number) => void
    ) {
      if (YearsState.isAnimationRunning) return
      const { x: bbX, y: bbY } = ref.current.getBoundingClientRect()
      const [xPos, yPos] = getMousePosition(event, [bbX, bbY])

      circles.forEach((circle) => {
        const isSelected = circle.year === YearsState.selectedYear
        const isHovered = circle.year === YearsState.hoveredYear
        circle.opacity = isSelected ? 1 : isHovered ? 0.4 : 0.1
      })
      setState(findPointedCircle(xPos, yPos)?.year)
      draw()
    }

    const [draw, startLoop, stopLoop] = useAnimationLoop(() => {
      const ctx = canvasRef.current?.getContext('2d')
      if (!ctx) return

      const { isAnimationRunning, selectedYear } = YearsState

      ctx.clearRect(0, 0, DIMENSIONS.w, DIMENSIONS.h)

      const pointedCircleIndex = findIndex(circleLabels, (d) => d.year === selectedYear)

      if (isAnimationRunning) {
        drawYearsLabel(ctx, animatedLabel)
      } else {
        drawYearsLegend(ctx, circleLabels[pointedCircleIndex])
      }

      circles.forEach((circle, i) => {
        const { x, y, radius, opacity, color } = circle
        drawCircle(ctx, { cx: x, cy: y, radius, opacity, strokeColor: color })
      })
    }, [challenge])

    useEffect(() => {
      draw()
      setup()
    }, [challenge])

    useEffect(() => {
      circles.forEach((circle) => {
        circle.opacity = circle.year === selectedYear ? 1 : 0.1
      })
      draw()
    }, [selectedYear, challenge])

    return (
      <Container>
        <canvas
          ref={canvasRef}
          className="pointer"
          width={DIMENSIONS.w}
          height={DIMENSIONS.h}
          onClick={(event) => {
            handleInteraction(event, canvasRef, (year) => {
              if (year) setSelectedYear(year)
            })
          }}
          onMouseMove={(event) => {
            handleInteraction(event, canvasRef, setHoveredYear)
          }}
        />

        {YearsState.isAnimationRunning || (
          <div className="flex flex-center gray300 w5 f6" style={{ paddingBottom: 36 }}>
            <SliderWithLabels
              className="w-100"
              min={YEARS_EXTENT[0]}
              max={YEARS_EXTENT[1]}
              step={1}
              value={selectedYear}
              showTooltip
              onChange={(value) => setSelectedYear(value)}
            />
          </div>
        )}
      </Container>
    )
  })
)
